import { useQuery } from "@tanstack/react-query";
import Repository from "./Repository";
import { DatePermissionParams } from "./types/Dashboards";
import { DashboardCheckIns } from "./types/DashboardCheckIns";
import { OvertimeWeeks } from "./types/OvertimeWeeks";
import { TimeOff } from "./types/TimeOff";
import { LeaderScore } from "./types/LeaderScore";
import { AverageTicket } from "./types/AverageTicket";
import { AverageComp } from "./types/AverageComp";
import { TeamDashboard } from "./types/Teams";

const executiveResource = "/dashboard/executive";
const teamLeaderResource = "/dashboard/team-leader";

export default {
  getCheckIns(datePermissionParams: DatePermissionParams) {
    return useQuery({
      queryKey: [
        DashboardQueryKeys.CHECK_INS,
        ...getQueryKeys(datePermissionParams),
      ],
      queryFn: () =>
        Repository.get<DashboardCheckIns>(
          `${getResource(datePermissionParams)}/check-ins`,
          {
            params: datePermissionParams,
          }
        ).then(res => res.data),
    });
  },
  getOvertimeWeeks(datePermissionParams: DatePermissionParams) {
    return useQuery({
      queryKey: [
        DashboardQueryKeys.OVERTIME_WEEKS,
        ...getQueryKeys(datePermissionParams),
      ],
      queryFn: () =>
        Repository.get<OvertimeWeeks>(
          `${getResource(datePermissionParams)}/overtime-weeks`,
          {
            params: datePermissionParams,
          }
        ).then(res => res.data),
    });
  },
  getTimeOff(datePermissionParams: DatePermissionParams) {
    return useQuery({
      queryKey: [
        DashboardQueryKeys.TIME_OFF,
        ...getQueryKeys(datePermissionParams),
      ],
      queryFn: () =>
        Repository.get<TimeOff>(
          `${getResource(datePermissionParams)}/time-off`,
          {
            params: datePermissionParams,
          }
        ).then(res => res.data),
    });
  },
  getLeaderScores(datePermissionParams: DatePermissionParams) {
    return useQuery({
      queryKey: [
        DashboardQueryKeys.LEADER_SCORES,
        ...getQueryKeys(datePermissionParams),
      ],
      queryFn: () =>
        Repository.get<LeaderScore>(
          `${getResource(datePermissionParams)}/leader-score`,
          {
            params: datePermissionParams,
          }
        ).then(res => res.data),
    });
  },
  getAverageTicket(datePermissionParams: DatePermissionParams) {
    return useQuery({
      queryKey: [
        DashboardQueryKeys.AVERAGE_TICKET,
        ...getQueryKeys(datePermissionParams),
      ],
      queryFn: () =>
        Repository.get<AverageTicket>(
          `${getResource(datePermissionParams)}/average-ticket`,
          {
            params: datePermissionParams,
          }
        ).then(res => res.data),
    });
  },
  getAverageComp(datePermissionParams: DatePermissionParams) {
    return useQuery({
      queryKey: [
        DashboardQueryKeys.AVERAGE_COMP,
        ...getQueryKeys(datePermissionParams),
      ],
      queryFn: () =>
        Repository.get<AverageComp>(
          `${getResource(datePermissionParams)}/average-comp`,
          {
            params: datePermissionParams,
          }
        ).then(res => res.data),
    });
  },
  getTeams(datePermissionParams: DatePermissionParams) {
    return useQuery({
      queryKey: [
        DashboardQueryKeys.TEAMS,
        ...getQueryKeys(datePermissionParams),
      ],
      queryFn: () =>
        Repository.get<Array<TeamDashboard>>(
          `${getResource(datePermissionParams)}/${
            datePermissionParams.executive ? "teams" : "members"
          }`,
          {
            params: datePermissionParams,
          }
        ).then(res => res.data),
    });
  },
};

function getQueryKeys(datePermissionParams: DatePermissionParams) {
  const keys = [];

  if (datePermissionParams.executive) {
    keys.push("exec");
  } else if (datePermissionParams.teamLeadId) {
    keys.push(datePermissionParams.teamLeadId);
  } else {
    keys.push("default");
  }

  if (datePermissionParams.is30Day) {
    keys.push("30day");
  } else if (datePermissionParams.is60Day) {
    keys.push("60day");
  } else if (datePermissionParams.is90Day) {
    keys.push("90day");
  } else {
    keys.push("default");
  }

  return keys;
}

function getResource(datePermissionParams: DatePermissionParams) {
  if (datePermissionParams.executive) {
    return executiveResource;
  } else if (datePermissionParams.teamLeadId) {
    return teamLeaderResource;
  }
}

export enum DashboardQueryKeys {
  CHECK_INS = "checkIns",
  OVERTIME_WEEKS = "overtimeWeeks",
  TIME_OFF = "timeOff",
  LEADER_SCORES = "leaderScore",
  AVERAGE_TICKET = "averageTicket",
  AVERAGE_COMP = "averageComp",
  TEAMS = "teams",
}
